/**
 * DataCacheContext
 *
 * Global React Context for caching customer, product, branch, and farm data
 * to reduce redundant database queries across the application.
 *
 * Key Features:
 * - In-memory caching of frequently accessed data
 * - localStorage persistence for faster subsequent loads
 * - Force refresh capability for invalidating stale data
 * - Cache invalidation after data mutations
 * - Debug logging for monitoring cache hits/misses
 * - Sequential preload support with progress tracking
 */

/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react'
import { fetchCustomers, fetchProducts, fetchProductTypes, fetchFarmInformation,
         fetchPriceTiers, fetchWholesaleThresholds, fetchVolumeDiscounts, fetchProductPrices,
         fetchBlockFormats, fetchBlockPrices, fetchActivePromotions,
         fetchGrowingMaterials, fetchGrowingMaterialPrices } from '../supabase-client'

const DataCacheContext = createContext()

const CACHE_VERSION = 1
const CACHE_TTL = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

/**
 * Load cache from localStorage
 */
const loadCacheFromStorage = () => {
  try {
    const stored = localStorage.getItem('odin_data_cache')
    if (!stored) return null

    const { version, timestamp, data } = JSON.parse(stored)

    // Check version compatibility
    if (version !== CACHE_VERSION) {
      console.log('[DataCache] Cache version mismatch, clearing cache')
      localStorage.removeItem('odin_data_cache')
      return null
    }

    // Check if cache has expired
    if (Date.now() - timestamp > CACHE_TTL) {
      console.log('[DataCache] Cache expired, clearing cache')
      localStorage.removeItem('odin_data_cache')
      return null
    }

    console.log('[DataCache] Loaded cache from localStorage')
    return data
  } catch (error) {
    console.error('[DataCache] Error loading from localStorage:', error)
    return null
  }
}

/**
 * Save cache to localStorage
 */
const saveCacheToStorage = (data) => {
  try {
    localStorage.setItem('odin_data_cache', JSON.stringify({
      version: CACHE_VERSION,
      timestamp: Date.now(),
      data
    }))
  } catch (error) {
    console.error('[DataCache] Error saving to localStorage:', error)
  }
}

export const DataCacheProvider = ({ children }) => {
  const initialCache = loadCacheFromStorage() || {
    customers: null,
    products: null,
    productTypes: null,
    farmInfo: null,
    priceTiers: null,
    wholesaleThresholds: null,
    volumeDiscounts: null,
    productPrices: null,
    blockFormats: null,
    blockPrices: null,
    growingMaterials: null,
    growingMaterialPrices: null,
    activePromotions: null,
    lastFetch: {
      customers: null,
      products: null,
      productTypes: null,
      farmInfo: null,
      priceTiers: null,
      wholesaleThresholds: null,
      volumeDiscounts: null,
      productPrices: null,
      blockFormats: null,
      blockPrices: null,
      growingMaterials: null,
      growingMaterialPrices: null,
      activePromotions: null
    }
  }

  const [cache, setCache] = useState(initialCache)
  const inflightRef = useRef({})

  /**
   * Generic cache getter with promise coalescing.
   * When multiple components call the same getter simultaneously on mount,
   * subsequent callers join the in-flight promise instead of firing duplicate fetches.
   */
  const getCachedData = useCallback(async (key, fetchFn, forceRefresh = false) => {
    // 1. Check in-memory cache
    let cachedData = null
    setCache(prev => {
      cachedData = prev[key]
      return prev
    })
    if (cachedData && !forceRefresh) {
      console.log(`[DataCache] Using cached ${key}`)
      return cachedData
    }

    // 2. If fetch already in-flight for this key, return same promise
    if (!forceRefresh && inflightRef.current[key]) {
      console.log(`[DataCache] Joining in-flight request for ${key}`)
      return inflightRef.current[key]
    }

    // 3. Start new fetch, store promise for dedup
    console.log(`[DataCache] Fetching ${key} from database`)
    inflightRef.current[key] = fetchFn()
      .then(data => {
        setCache(prev => {
          const updated = {
            ...prev,
            [key]: data,
            lastFetch: { ...prev.lastFetch, [key]: Date.now() }
          }
          saveCacheToStorage(updated)
          return updated
        })
        return data
      })
      .finally(() => {
        delete inflightRef.current[key]
      })

    return inflightRef.current[key]
  }, [])

  const getCustomers = useCallback((forceRefresh) => getCachedData('customers', fetchCustomers, forceRefresh), [getCachedData])
  const getProducts = useCallback((forceRefresh) => getCachedData('products', fetchProducts, forceRefresh), [getCachedData])
  const getProductTypes = useCallback((forceRefresh) => getCachedData('productTypes', fetchProductTypes, forceRefresh), [getCachedData])
  const getFarmInfo = useCallback((forceRefresh) => getCachedData('farmInfo', fetchFarmInformation, forceRefresh), [getCachedData])
  const getPriceTiers = useCallback((forceRefresh) => getCachedData('priceTiers', fetchPriceTiers, forceRefresh), [getCachedData])
  const getWholesaleThresholds = useCallback((forceRefresh) => getCachedData('wholesaleThresholds', fetchWholesaleThresholds, forceRefresh), [getCachedData])
  const getVolumeDiscounts = useCallback((forceRefresh) => getCachedData('volumeDiscounts', fetchVolumeDiscounts, forceRefresh), [getCachedData])
  const getProductPrices = useCallback((forceRefresh) => getCachedData('productPrices', fetchProductPrices, forceRefresh), [getCachedData])
  const getBlockFormats = useCallback((forceRefresh) => getCachedData('blockFormats', fetchBlockFormats, forceRefresh), [getCachedData])
  const getBlockPrices = useCallback((forceRefresh) => getCachedData('blockPrices', fetchBlockPrices, forceRefresh), [getCachedData])
  const getGrowingMaterials = useCallback((forceRefresh) => getCachedData('growingMaterials', fetchGrowingMaterials, forceRefresh), [getCachedData])
  const getGrowingMaterialPrices = useCallback((forceRefresh) => getCachedData('growingMaterialPrices', fetchGrowingMaterialPrices, forceRefresh), [getCachedData])
  const getActivePromotions = useCallback((forceRefresh) => getCachedData('activePromotions', fetchActivePromotions, forceRefresh), [getCachedData])

  /**
   * Invalidate cache for specific key or all keys
   * @param {string} key - Cache key to invalidate ('customers', 'products', 'productTypes', 'farmInfo', 'priceTiers', 'wholesaleThresholds', 'volumeDiscounts', 'productPrices', 'all')
   */
  const invalidateCache = useCallback((key) => {
    console.log(`[DataCache] Invalidating cache: ${key}`)
    if (key === 'all') {
      inflightRef.current = {}
      const newCache = {
        customers: null,
        products: null,
        productTypes: null,
        farmInfo: null,
        priceTiers: null,
        wholesaleThresholds: null,
        volumeDiscounts: null,
        productPrices: null,
        blockFormats: null,
        blockPrices: null,
        growingMaterials: null,
        growingMaterialPrices: null,
        activePromotions: null,
        lastFetch: { customers: null, products: null, productTypes: null, farmInfo: null, priceTiers: null, wholesaleThresholds: null, volumeDiscounts: null, productPrices: null, blockFormats: null, blockPrices: null, growingMaterials: null, growingMaterialPrices: null, activePromotions: null }
      }
      setCache(newCache)
      localStorage.removeItem('odin_data_cache')
    } else {
      delete inflightRef.current[key]
      setCache(prev => {
        const updated = {
          ...prev,
          [key]: null,
          lastFetch: { ...prev.lastFetch, [key]: null }
        }
        saveCacheToStorage(updated)
        return updated
      })
    }
  }, [])

  /**
   * Update cache state and persist to localStorage
   * Called whenever cache data is fetched
   */
  const updateCache = useCallback((updates) => {
    setCache(prev => {
      const updated = { ...prev, ...updates }
      saveCacheToStorage(updated)
      return updated
    })
  }, [])

  const snapshot = useMemo(() => ({
    customers: cache.customers,
    products: cache.products,
    productTypes: cache.productTypes,
    priceTiers: cache.priceTiers,
    wholesaleThresholds: cache.wholesaleThresholds,
    volumeDiscounts: cache.volumeDiscounts,
    productPrices: cache.productPrices,
    farmInfo: cache.farmInfo,
    blockFormats: cache.blockFormats,
    blockPrices: cache.blockPrices,
    growingMaterials: cache.growingMaterials,
    growingMaterialPrices: cache.growingMaterialPrices,
    activePromotions: cache.activePromotions
  }), [
    cache.customers,
    cache.products,
    cache.productTypes,
    cache.priceTiers,
    cache.wholesaleThresholds,
    cache.volumeDiscounts,
    cache.productPrices,
    cache.farmInfo,
    cache.blockFormats,
    cache.blockPrices,
    cache.growingMaterials,
    cache.growingMaterialPrices,
    cache.activePromotions
  ])

  const value = useMemo(() => ({
    getCustomers,
    getProducts,
    getProductTypes,
    getFarmInfo,
    getPriceTiers,
    getWholesaleThresholds,
    getVolumeDiscounts,
    getProductPrices,
    getBlockFormats,
    getBlockPrices,
    getGrowingMaterials,
    getGrowingMaterialPrices,
    getActivePromotions,
    invalidateCache,
    updateCache,
    cacheStats: cache.lastFetch,
    snapshot
  }), [
    getCustomers,
    getProducts,
    getProductTypes,
    getFarmInfo,
    getPriceTiers,
    getWholesaleThresholds,
    getVolumeDiscounts,
    getProductPrices,
    getBlockFormats,
    getBlockPrices,
    getGrowingMaterials,
    getGrowingMaterialPrices,
    getActivePromotions,
    invalidateCache,
    updateCache,
    cache.lastFetch,
    snapshot
  ])

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  )
}

/**
 * Hook to access data cache context
 * @returns {Object} Cache context with getters and invalidation function
 * @throws {Error} If used outside DataCacheProvider
 */
export const useDataCache = () => {
  const context = useContext(DataCacheContext)
  if (!context) {
    throw new Error('useDataCache must be used within DataCacheProvider')
  }
  return context
}
